import { lazyZodSchema } from './lazy-zod';

export const FieldTypeSchema = lazyZodSchema((z) => z.enum([
  "text",
  "number",
  "boolean",
  "select",
  "multiSelect",
  "date",
  "time",
  "datetime",
  "photo",
  "multiPhoto",
  "barcode",
  "multiBarcode",
  "signature",
  "checklist",
  "checklistWithNotes",
  "gps",
  "nfc",
]));

export const FieldValidationSchema = lazyZodSchema((z) => z.object({
  required: z.boolean().optional(),
  range: z.object({ min: z.number().optional(), max: z.number().optional() }).optional(),
  regex: z.string().optional(),
  expectedValue: z.any().optional(),
}).optional());

export const OptionsSourceSchema = lazyZodSchema((z) => z.discriminatedUnion("type", [
  z.object({
    type: z.literal("static"),
    options: z.array(z.object({ label: z.string(), value: z.string() })).describe("List of static option choices"),
  }),
  z.object({
    type: z.literal("firestore"),
    id: z.string().describe("Option set ID or collection reference ID"),
    optionsSetId: z.string().optional().describe("Reference to the option set id in optionsSets"),
    collection: z.string().optional(),
    document: z.string().optional(),
    options: z.any().optional(),
  })
]).optional().nullable());

export const FormFieldSchema = lazyZodSchema((z) => z.object({
  id: z.string().describe("Unique field ID within form, lowercase snake_case, e.g. operator_name"),
  key: z.string().describe("Database key matching id, lowercase snake_case, e.g. operator_name"),
  label: z.string().describe("Human-readable field label, e.g. Operator / Driver Name"),
  type: FieldTypeSchema,
  defaultValue: z.any().optional().nullable(),
  hidden: z.boolean().optional().nullable(),
  readOnly: z.boolean().optional().nullable(),
  includeInPdf: z.boolean().optional().nullable(),
  includeMetadataInPdf: z.boolean().optional().nullable(),
  displayIf: z.array(z.object({
    fieldId: z.string(),
    operator: z.enum(["equals", "notEquals", "greaterThan", "lessThan", "in", "notIn"]),
    value: z.any()
  })).optional().nullable().describe("Conditions for conditional visibility. Evaluated as an OR if multiple conditions are provided."),
  canPrepopulate: z.boolean().optional().nullable().describe("If true, the app remembers the last submitted value and allows the user to auto-fill it next time (useful for vehicle numbers, inspector names, etc)."),
  checklistNoteInputType: z.enum(["text", "number"]).optional().nullable().describe("If type is checklistWithNotes, specifies the type of the note input (text or number)."),
  dependencies: z.array(z.object({
    fieldId: z.string().describe("The ID of the target field to modify"),
    action: z.enum(["update", "reset"]),
    value: z.any().optional()
  })).optional().nullable().describe("Rules for automatically updating or resetting other fields when this field's value changes."),
  validation: FieldValidationSchema.nullable().optional(),
  optionsSource: OptionsSourceSchema,
}));

export const FormSectionSchema = lazyZodSchema((z) => z.object({
  id: z.string().describe("Section ID, e.g. section_general or sec_inspection"),
  title: z.string().describe("Section display title, e.g. General Information"),
  fields: z.array(z.string()).describe("Array of field IDs that belong to this section in display order"),
}));

export const FlexFormTemplateSchema = lazyZodSchema((z) => z.object({
  id: z.string().optional().describe("Form template ID prefixed with 'form-', e.g. form-daily-field-log"),
  name: z.string().describe("Form name, e.g. Daily Field Operations Log"),
  description: z.string().optional().nullable().describe("Concise description of the form's business purpose"),
  companyId: z.string().optional().describe("Company ID that owns this template"),
  active: z.boolean().optional().nullable().describe("Whether this template is currently active"),
  archived: z.boolean().optional().nullable().default(false).describe("Whether this template is archived"),
  prefix: z.string().optional().nullable().describe("Single digit prefix for document numbering, e.g. '1' or '2'"),
  notificationSubjectLine: z.string().optional().nullable().describe("Email/Notification subject template, e.g. Daily Log: {{submitterName}}"),
  notificationBody: z.string().optional().nullable().describe("Notification body summary text"),
  notificationEmails: z.array(z.string()).optional().nullable().describe("Array of email addresses or Handlebars expressions to send PDF notifications to."),
  notificationWhatsappNumbers: z.array(z.string()).optional().nullable().describe("Array of WhatsApp phone numbers (e.g. '+27831234567') or Handlebars expressions for notifications."),
  guestSubmitterLabel: z.string().optional().nullable(),
  hideNullishInPdf: z.boolean().optional().nullable(),
  isPublicCaptureEnabled: z.boolean().optional().nullable(),
  requireAuthentication: z.boolean().optional().nullable(),
  pdfLogoMaxWidth: z.number().optional().nullable(),
  pdfPhotoMaxWidth: z.number().optional().nullable(),
  pdfSignatureMaxWidth: z.number().optional().nullable(),
  moduleId: z.string().optional().nullable(),
  moduleMetadata: z.any().optional(),
  formLevelValidations: z.array(z.object({
    expectedValues: z.record(z.string(), z.any()).optional().nullable(),
    ranges: z.record(z.string(), z.object({ min: z.number(), max: z.number() })).optional().nullable(),
    regexes: z.record(z.string(), z.string()).optional().nullable(),
    message: z.string()
  })).optional().nullable(),
  fields: z.array(FormFieldSchema).min(1).describe("List of fields in the form"),
  sections: z.array(FormSectionSchema).optional().nullable().describe("Layout sections grouping the fields"),
}));

export const FlexOptionItemSchema = lazyZodSchema((z) => z.object({
  label: z.string().describe("Display label shown in UI dropdown"),
  value: z.string().describe("Value code saved to database"),
  metadata: z.record(z.string(), z.any()).optional().nullable().describe("Optional extra metadata"),
}));

export const FlexOptionSetSchema = lazyZodSchema((z) => z.object({
  id: z.string().optional().describe("Option set ID prefixed with 'os-', e.g. os-equipment-types"),
  companyId: z.string().optional().describe("Company ID that owns this option set"),
  name: z.string().describe("Option set name, e.g. Equipment & Vehicle Types"),
  label: z.string().optional(),
  options: z.array(FlexOptionItemSchema).min(1).describe("List of selectable options"),
  moduleId: z.string().optional().nullable(),
  linkedFormTemplateId: z.string().optional(),
  mappingConfig: z.any().optional(),
  autoSync: z.boolean().optional(),
  lastSyncedAt: z.any().optional(),
  moduleMetadata: z.any().optional(),
  metadata: z.record(z.string(), z.any()).optional().nullable(),
}));

export const RuntimeFilterSchema = lazyZodSchema((z) => z.object({
  id: z.string().describe("Parameter variable name (e.g. 'filterStoreroom', 'filterBlock')"),
  label: z.string().describe("UI Label displayed above input"),
  type: z.enum(["dropdown", "number", "text", "toggle"]).describe("Input control type"),
  defaultValue: z.any().optional().describe("Initial default value (e.g. 'ALL', 14, 0)"),
  placeholder: z.string().optional().describe("Placeholder text"),
  options: z.array(z.string()).optional().describe("Static options list (optional)"),
  optionsSource: z.string().optional().describe("Dynamic OptionSet identifier (e.g. 'optionSet:storerooms')"),
  min: z.number().optional().describe("Min value (for number inputs)"),
  max: z.number().optional().describe("Max value (for number inputs)"),
  step: z.number().optional().describe("Increment step (for number inputs)"),
  required: z.boolean().optional().describe("Is input mandatory (default false)"),
}));

export const FlexReportSchema = lazyZodSchema((z) => z.object({
  id: z.string().optional().describe("Report ID, e.g. report-daily-harvest"),
  companyId: z.string().optional().describe("Company ID that owns this report"),
  name: z.string().optional().describe("Report Name, e.g. Daily Harvest Summary"),
  reportShortName: z.string().optional().describe("Report Short Code/Name"),
  description: z.string().optional().nullable().describe("What this report shows"),
  reportDescription: z.string().optional().nullable().describe("Legacy report description"),
  dataSource: z.enum(["bigquery", "firestore"]).or(z.string()).optional().nullable().default("firestore").describe("Data source for the report"),
  includedTemplates: z.array(z.string()).optional().nullable().describe("Form template IDs referenced by this report"),
  reportSelectStatement: z.string().optional().nullable().describe("Valid BigQuery SQL for the report"),
  reportTemplateLink: z.string().optional().describe("Template location"),
  isActive: z.boolean().default(true),
  renderingType: z.string().optional().default("xlsx").describe("Must be 'xlsx'"),
  moduleId: z.string().optional().nullable(),
  moduleName: z.string().optional().nullable(),
  moduleMetadata: z.any().optional(),
  runtimeFilters: z.array(RuntimeFilterSchema).optional().nullable().describe("Dynamic runtime filter definitions"),
}));

export const FlexModuleSchema = lazyZodSchema((z) => z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().optional(),
  category: z.string(),
  templates: z.array(FlexFormTemplateSchema),
  optionSets: z.array(FlexOptionSetSchema),
  reports: z.array(FlexReportSchema),
}));

export const FlexModuleDraftSchema = lazyZodSchema((z) => z.object({
  id: z.string().optional(),
  claimToken: z.string(),
  moduleName: z.string(),
  companyName: z.string().optional(),
  industry: z.string().optional(),
  status: z.enum(["draft", "claimed", "installed"]).default("draft"),
  createdAt: z.any(),
  claimedAt: z.any().optional(),
  claimedByCompanyId: z.string().optional(),
  claimedByUserId: z.string().optional(),
  conversationId: z.string().optional(),
  payload: FlexModuleSchema,
}));
